import { useCallback, useEffect, useRef } from "react";
import { socket } from "src/App";
import { useAuthContext } from "src/auth/useAuthContext";

const NEW_ORDER_TITLE = "🔔 New Order Received";
const CLEAR_EVENT_NAME = "egora:clear-incoming-order-title-alert";

const getId = (value) => value?.id || value?._id || value;

const isSameId = (firstId, secondId) => {
    if (!firstId || !secondId) return false;
    return String(firstId) === String(secondId);
};

const isOrderForCurrentUser = (order, user) => {
    const orderVenueId = getId(order?.venueId);
    const orderLevelId = getId(order?.levelId);

    const isSameVenue = isSameId(user?.venueId, orderVenueId);

    const isSameLevel =
        isSameId(user?.levelId, orderLevelId) ||
        user?.role === "admin" ||
        order?.isSuperAdmin;

    return isSameVenue && isSameLevel;
};

export const clearIncomingOrderTitleAlert = () => {
    window.dispatchEvent(new Event(CLEAR_EVENT_NAME));
};

export default function GlobalOrderTitleAlert() {
    const { user } = useAuthContext();

    const intervalRef = useRef(null);
    const baseTitleRef = useRef(document.title || "Egora");
    const isAlertActiveRef = useRef(false);
    const lastOrderIdRef = useRef(null);

    const clearTitleInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const stopTitleAlert = useCallback(() => {
        isAlertActiveRef.current = false;
        lastOrderIdRef.current = null;

        clearTitleInterval();

        document.title = baseTitleRef.current || "Egora";
    }, [clearTitleInterval]);

    const startTitleAlert = useCallback(
        (order) => {
            const orderId = getId(order);

            if (
                orderId &&
                isAlertActiveRef.current &&
                lastOrderIdRef.current === orderId
            ) {
                return;
            }

            if (!isAlertActiveRef.current && document.title !== NEW_ORDER_TITLE) {
                baseTitleRef.current = document.title || baseTitleRef.current || "Egora";
            }

            isAlertActiveRef.current = true;
            lastOrderIdRef.current = orderId;

            clearTitleInterval();

            let showNewOrderTitle = false;

            document.title = NEW_ORDER_TITLE;

            intervalRef.current = setInterval(() => {
                document.title = showNewOrderTitle
                    ? NEW_ORDER_TITLE
                    : baseTitleRef.current;

                showNewOrderTitle = !showNewOrderTitle;
            }, 2000);
        },
        [clearTitleInterval]
    );

    useEffect(() => {
        const titleElement = document.querySelector("title");

        if (!titleElement) return undefined;

        const observer = new MutationObserver(() => {
            if (!isAlertActiveRef.current && document.title !== NEW_ORDER_TITLE) {
                baseTitleRef.current = document.title || baseTitleRef.current || "Egora";
            }
        });

        observer.observe(titleElement, {
            childList: true,
            characterData: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!user?.venueId || !user?.levelId) return undefined;

        const handleIncomingOrder = (order) => {
            if (!isOrderForCurrentUser(order, user)) return;

            startTitleAlert(order);
        };

        socket.on("IncomingOrdersForPos", handleIncomingOrder);

        return () => {
            socket.off("IncomingOrdersForPos", handleIncomingOrder);
        };
    }, [
        user?.venueId,
        user?.levelId,
        user?.role,
        startTitleAlert,
    ]);

    useEffect(() => {
        window.addEventListener(CLEAR_EVENT_NAME, stopTitleAlert);

        return () => {
            window.removeEventListener(CLEAR_EVENT_NAME, stopTitleAlert);
            clearTitleInterval();
        };
    }, [stopTitleAlert, clearTitleInterval]);

    return null;
}